import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { lazy, useEffect } from 'react'
import MainLayout from './components/layout/MainLayout'
import PublicLayout from './components/layout/PublicLayout'
import ProtectedRoute from './components/ProtectedRoute'
import { PlanGate } from './components/ui/PlanGate'
import SmartDocsRoute from './components/SmartDocsRoute'

const retryLazy = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3
): Promise<{ default: T }> => {
  return new Promise<{ default: T }>((resolve, reject) => {
    const attempt = (remainingRetries: number) => {
      importFn()
        .then((module: { default: T }) => resolve(module))
        .catch((error: any) => {
          if (remainingRetries > 0) {
            const delay = Math.pow(2, retries - remainingRetries) * 100
            console.warn(`Failed to load module, retrying in ${delay}ms... (${remainingRetries} retries left)`)
            setTimeout(() => attempt(remainingRetries - 1), delay)
          } else {
            reject(error)
          }
        })
    }
    attempt(retries)
  })
}

import { Landing, Login, Signup } from './pages'
const CalendarEmbed = lazy(() => retryLazy(() => import('./pages/SocialPlanner/CalendarEmbed')))
const EventBooking = lazy(() => retryLazy(() => import('./pages/SocialPlanner/EventBooking')))
const ConsignmentPage = lazy(() => retryLazy(() => import('./pages/Consignment/ConsignmentPage')))

const RootEntry = () => {
  const location = useLocation()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const shop = params.get('shop')
    const host = params.get('host')
    const hmac = params.get('hmac')
    const embedded = params.get('embedded')
    const session = params.get('session')

    if (import.meta.env.DEV) {
      console.log('[TCGEasy] RootEntry check:', {
        hasShop: !!shop,
        hasHost: !!host,
        hasHmac: !!hmac,
        hasEmbedded: !!embedded,
        hasSession: !!session,
        willRedirect: !!(shop && (host || hmac || embedded || session)),
      })
    }

    if (shop && (host || hmac || embedded || session)) {
      const query = location.search.startsWith('?') ? location.search.slice(1) : location.search
      if (import.meta.env.DEV) console.log('[TCGEasy] Forwarding to /api/shopify/launch')
      window.location.replace(`/api/shopify/launch?${query}`)
    }
  }, [location.search])

  return <Landing />
}

const AboutMe = lazy(() => retryLazy(() => import('./pages/Public/AboutMe/AboutMe')))
const Features = lazy(() => retryLazy(() => import('./pages/Public/Features/Features')))
const UseCasePage = lazy(() => retryLazy(() => import('./pages/Public/UseCase/UseCasePage')))
const Pricing = lazy(() => retryLazy(() => import('./pages/Public/Pricing/Pricing')))
const Contact = lazy(() => retryLazy(() => import('./pages/Public/Contact/Contact')))
const VerifyEmail = lazy(() => retryLazy(() => import('./pages/VerifyEmail/VerifyEmail')))
const ForgotPassword = lazy(() => retryLazy(() => import('./pages/ForgotPassword/ForgotPassword')))
const ResetPassword = lazy(() => retryLazy(() => import('./pages/ResetPassword/ResetPassword')))
const Privacy = lazy(() => retryLazy(() => import('./pages/Public/Privacy/Privacy')))
const Terms = lazy(() => retryLazy(() => import('./pages/Public/Terms/Terms')))
const FAQ = lazy(() => retryLazy(() => import('./pages/Public/FAQ/FAQ')))
const Screencast = lazy(() => retryLazy(() => import('./pages/Screencast/Screencast')))
const ScanProduct = lazy(() => retryLazy(() => import('./pages/Scan/ScanProduct')))
const StoreSearch = lazy(() => retryLazy(() => import('./pages/StoreSearch/StoreSearch')))
const BasketPage = lazy(() => retryLazy(() => import('./pages/Basket/BasketPage')))
const Changelog = lazy(() => retryLazy(() => import('./pages/Public/Changelog/Changelog')))
const NotFound = lazy(() => retryLazy(() => import('./pages/Public/NotFound/NotFound')))

const Dashboard = lazy(() => retryLazy(() => import('./pages/Dashboard/Dashboard')))
const CSVImport = lazy(() => retryLazy(() => import('./pages/Inventories/CSVImport/CSVImport')))
const CustomProductCreate = lazy(() => retryLazy(() => import('./pages/Inventories/CustomProduct/CustomProductCreate')))
const Inventory = lazy(() => retryLazy(() => import('./pages/Inventories/Inventory/Inventory')))
const InventoryTags = lazy(() => retryLazy(() => import('./pages/Inventories/Tags/InventoryTags')))
const Settings = lazy(() => retryLazy(() => import('./pages/Settings/Account')))
const PaymentHistory = lazy(() => retryLazy(() => import('./pages/PaymentHistory/PaymentHistory')))
const QRCodeGenerator = lazy(() => retryLazy(() => import('./pages/QR/QRCodeGenerator')))
const Support = lazy(() => retryLazy(() => import('./pages/Support/Support')))
const AdminTickets = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminTickets/AdminTickets')))
const AdminReview = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminReview/AdminReview')))
const AdminOrganizations = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminOrganizations/AdminOrganizations')))
const AdminDealCodes = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminDealCodes/AdminDealCodes')))
const AdminMetrics = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminMetrics/AdminMetrics')))
const AdminTest = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminTest/AdminTest')))
const CardScanner = lazy(() => retryLazy(() => import('./pages/Scanner/CardScanner')))
const CenteringCheck = lazy(() => retryLazy(() => import('./pages/CenteringCheck/CenteringCheck')))
const AdminAnnouncements = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminAnnouncements/AdminAnnouncements')))
const SocialPlannerComposer = lazy(() => retryLazy(() => import('./pages/SocialPlanner/SocialPlannerComposer')))
const AdminDatabase = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminDatabase/AdminDatabase')))
const AdminChangelogs = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminChangelogs/AdminChangelogs')))
const AdminPlanLimits = lazy(() => retryLazy(() => import('./pages/SuperAdmin/AdminPlanLimits/AdminPlanLimits')))
const TransactionCreate = lazy(() => retryLazy(() => import('./pages/Transactions/CreateTransaction')))
const CardsSearchResults = lazy(() => retryLazy(() => import('./pages/Inventories/Search/CardsSearchResults')))
const TransactionsPage = lazy(() => retryLazy(() => import('./pages/Transactions/Transactions')))
const OverviewAnalytics = lazy(() => retryLazy(() => import('./pages/Analytics/OverviewAnalytics')))
const OnlineAnalytics = lazy(() => retryLazy(() => import('./pages/Analytics/OnlineAnalytics')))
const InStoreAnalytics = lazy(() => retryLazy(() => import('./pages/Analytics/InStoreAnalytics')))
const InventoryAnalytics = lazy(() => retryLazy(() => import('./pages/Analytics/InventoryAnalytics')))
const EbayAuctions = lazy(() => retryLazy(() => import('./pages/Auctions/EbayAuctions')))
const SocialPlannerLayout = lazy(() => retryLazy(() => import('./pages/SocialPlanner/SocialPlannerLayout')))
const SocialPlannerPage = lazy(() => retryLazy(() => import('./pages/SocialPlanner/SocialPlanner')))
const SocialPlannerCalendarPage = lazy(() => retryLazy(() => import('./pages/SocialPlanner/SocialPlannerCalendar')))
const ScreensaverPage = lazy(() => retryLazy(() => import('./pages/Tools/Screensaver/ScreensaverPage')))
const ScreensaverDisplayPage = lazy(() => retryLazy(() => import('./pages/Tools/Screensaver/ScreensaverDisplayPage')))


/** Redirect that keeps the query string (e.g. ?draftId=… on legacy lab links). */
const RedirectWithSearch = ({ to }: { to: string }) => {
  const location = useLocation()
  return <Navigate to={{ pathname: to, search: location.search }} replace />
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><RootEntry /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutMe /></PublicLayout>} />

      <Route path="/features" element={<PublicLayout><Features /></PublicLayout>} />
      <Route path="/shopify-card-stores" element={<PublicLayout><UseCasePage page="shopify" /></PublicLayout>} />
      <Route path="/in-store-sales" element={<PublicLayout><UseCasePage page="inStore" /></PublicLayout>} />
      <Route path="/supported-games" element={<PublicLayout><UseCasePage page="games" /></PublicLayout>} />
      <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/verify-email" element={<PublicLayout><VerifyEmail /></PublicLayout>} />
      <Route path="/documentation" element={<SmartDocsRoute />} />
      <Route path="/docs" element={<Navigate to="/documentation" replace />} />
      <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
      <Route path="/tos" element={<PublicLayout><Terms /></PublicLayout>} />
      <Route path="/terms" element={<Navigate to="/tos" replace />} />
      <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
      <Route path="/changelog" element={<PublicLayout><Changelog /></PublicLayout>} />
      <Route path="/screencast" element={<PublicLayout><Screencast /></PublicLayout>} />

      <Route path="/scan/:productId" element={<ScanProduct />} />

      <Route path="/stores" element={<StoreSearch />} />
      <Route path="/stores/:organizationId" element={<StoreSearch />} />

      <Route path="/basket/:code" element={<BasketPage />} />

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<Navigate to="/signup" replace />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Inventory />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory/products"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Inventory />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory/tags"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InventoryTags />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory/add"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CardsSearchResults />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory/custom"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CustomProductCreate />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory/csv"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CSVImport />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/import-csv"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CSVImport />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Settings />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* The old account page. Stripe and Shopify return links sent before the move still point
          here, with ?subscription=… that Settings acts on, so keep the query string. */}
      <Route path="/account" element={<RedirectWithSearch to="/settings" />} />

      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OverviewAnalytics />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics/online"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OnlineAnalytics />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics/in-store"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InStoreAnalytics />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics/inventory"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InventoryAnalytics />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/consignment"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConsignmentPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/auctions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EbayAuctions />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Social Hub — shared QR-style side-tab shell wrapping Calendar / Posts /
          Create Post. Routes are preserved so deep links keep working. The
          public iframe embed (/embed/calendar/:orgId) is a separate route. */}
      <Route
        element={
          <ProtectedRoute requiredRole={['super_admin', 'owner', 'admin', 'manager', 'staff']}>
            <MainLayout>
              <SocialPlannerLayout />
            </MainLayout>
          </ProtectedRoute>
        }
      >
        <Route
          path="/social-planner"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'owner', 'admin', 'manager']}>
              <PlanGate capability="socialPosts" title="Social posts">
                <SocialPlannerPage />
              </PlanGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/social-planner/calendar"
          element={<SocialPlannerCalendarPage />}
        />
        <Route
          path="/social-planner/composer"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'owner', 'admin', 'manager']}>
              <PlanGate capability="socialPosts" title="Social posts">
                <SocialPlannerComposer />
              </PlanGate>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="/payment-history"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PaymentHistory />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/qr"
        element={
          <ProtectedRoute>
            <MainLayout>
              <QRCodeGenerator />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Card scanner (beta): open to every signed-in role and plan (batch mode is Professional+). Recognition runs on the device; what it
          writes (a trade, inventory) goes through the same server routes and plan gating as the
          pages that normally do it. The old Tools link redirects, keeping its query string. */}
      <Route
        path="/scanner"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CardScanner />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/tools/scanner" element={<RedirectWithSearch to="/scanner" />} />

      {/* Centering check (beta): Enterprise. Measures on the device and stores nothing, so the plan gate is here only. */}
      <Route
        path="/scanner/centering"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PlanGate capability="centering" title="Centering Check">
                <CenteringCheck />
              </PlanGate>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tools/screensaver"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PlanGate capability="storeDisplay" title="Store Display">
                <ScreensaverPage />
              </PlanGate>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/*
        The shop-floor display.
        Deliberately outside MainLayout: it owns the whole viewport, and a sidebar behind a
        customer-facing screen is both wasted pixels and a way into the admin tool. Still behind
        ProtectedRoute, because it renders live stock and shelf prices for one organisation.
      */}
      <Route
        path="/tools/screensaver/display"
        element={
          <ProtectedRoute>
            <PlanGate capability="storeDisplay" title="Store Display">
              <ScreensaverDisplayPage />
            </PlanGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/screensaver"
        element={<Navigate to="/tools/screensaver" replace />}
      />


      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Support />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/scanner"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <CardScanner />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/tickets"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminTickets />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/review"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminReview />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/organizations"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminOrganizations />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/deal-codes"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminDealCodes />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/metrics"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminMetrics />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/test"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminTest />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/announcements"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminAnnouncements />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/social-planner/lab"
        element={<RedirectWithSearch to="/social-planner/composer" />}
      />

      <Route
        path="/super-admin/social-planner-lab"
        element={<RedirectWithSearch to="/social-planner/composer" />}
      />

      <Route
        path="/embed/calendar/:orgId"
        element={<CalendarEmbed />}
      />

      {/* Public "manage my booking" page. The token in the path is the credential, so this
          route is deliberately outside ProtectedRoute — registrants have no account. */}
      <Route
        path="/events/booking/:token"
        element={<EventBooking />}
      />

      <Route
        path="/super-admin/database"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminDatabase />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/changelogs"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminChangelogs />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/plan-limits"
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <MainLayout>
              <AdminPlanLimits />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/search/cards"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CardsSearchResults />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/transactions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Outlet />
            </MainLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<TransactionsPage view="all" />} />
        <Route path="create" element={<TransactionCreate />} />
        <Route path="orders" element={<TransactionsPage view="orders" />} />
        <Route path="in-store" element={<TransactionsPage view="in-store" />} />
      </Route>

      <Route
        path="*"
        element={
          <PublicLayout>
            <NotFound />
          </PublicLayout>
        }
      />
    </Routes>
  )
}
